import { Component, ChangeDetectionStrategy, inject, signal, effect, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import {
  ButtonDirective,
  CardBodyComponent,
  CardComponent,
  CardGroupComponent,
  ColComponent,
  ContainerComponent,
  RowComponent,
  SpinnerComponent,
  TextColorDirective
} from '@coreui/angular-pro';
import { IconModule } from '@coreui/icons-angular';
import { Logger, LogoComponent } from 'shared';
import { TranslatePipe } from 'i18n';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-confirm-email-page',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    ContainerComponent,
    RowComponent,
    ColComponent,
    CardGroupComponent,
    TextColorDirective,
    CardComponent,
    CardBodyComponent,
    ButtonDirective,
    IconModule,
    SpinnerComponent,
    LogoComponent,
    TranslatePipe,
    IconModule
  ],
  templateUrl: './confirm-email-page.component.html',
  styleUrls: ['./confirm-email-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ConfirmEmailPageComponent {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private http = inject(HttpClient);
  private destroyRef = inject(DestroyRef);
  protected logger = new Logger('ConfirmEmailPageComponent');

  // ────────────────────────────────────────────────
  // Reactive state
  // ────────────────────────────────────────────────
  loading = signal<boolean>(true);
  success = signal<boolean>(false);
  error = signal<string | null>(null);

  // ────────────────────────────────────────────────
  // Effects
  // ────────────────────────────────────────────────
  constructor() {
    // React to route params (token) once
    effect(() => {
      const token = this.route.snapshot.params['token'];

      if (!token) {
        this.loading.set(false);
        this.error.set('Invalid confirmation link');
        this.logger.warn('No token provided in URL');
        return;
      }

      this.logger.debug('Attempting email confirmation with token:', token);
      this.confirmEmail(token);
    });
  }

  // ────────────────────────────────────────────────
  // Core logic
  // ────────────────────────────────────────────────
  private confirmEmail(token: string): void {
    this.loading.set(true);
    this.error.set(null);

    this.http.get<{ success: boolean; message?: string; error?: string }>(`/auth/confirm_email/${token}`)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.logger.debug('Email confirmation response:', response);

          if (response.success) {
            this.success.set(true);
            this.loading.set(false);

            // Auto-redirect after 3 seconds
            setTimeout(() => {
              this.router.navigate(['/login']);
            }, 3000);
          } else {
            this.handleError(response.error || 'Confirmation failed');
          }
        },
        error: err => {
          this.handleError(err.error?.error || 'Email confirmation failed');
        }
      });
  }

  private handleError(msg: string): void {
    this.error.set(msg);
    this.success.set(false);
    this.loading.set(false);
    this.logger.error('Email confirmation error:', msg);
  }
}